import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Lock, Calendar, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { ORG_TYPES, TIMELINES } from "@/data/catalogue";

const ATTENDEES = ["Just me", "2–3", "4–8", "9 or more"];
const FORMATS = ["In person", "Video call", "Either"];

const INCLUDES = [
  "90-minute session for your board or executive team",
  "Governance readiness & data-sovereignty obligations for your context",
  "Where BIGTEK's catalogue fits your program and procurement",
  "Clear, no-obligation next steps",
];

export default function BookBriefing() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    org_type: "",
    role: "",
    attendees: "",
    format: "",
    timeframe: "",
    cover: "",
    challenge: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organisation || !form.org_type) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!form.cover) {
      toast.error("Please tell us what you'd like the briefing to cover.");
      return;
    }
    if (!form.consent) {
      toast.error("Please provide consent to continue.");
      return;
    }
    setLoading(true);
    try {
      const message = [
        `Attendees: ${form.attendees || "—"}`,
        `Preferred format: ${form.format || "—"}`,
        `Preferred timeframe: ${form.timeframe || "—"}`,
        "",
        `What to cover: ${form.cover}`,
        `Biggest governance challenge: ${form.challenge || "—"}`,
      ].join("\n");
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        organisation: form.organisation,
        org_type: form.org_type,
        role: form.role,
        timeline: form.timeframe,
        product_slug: "executive-briefing",
        product_name: "Executive Briefing (From $9,500 + GST)",
        message,
        consent: form.consent,
      });
      setDone(true);
      toast.success("Briefing request received — check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 lg:px-8 lg:py-16">
      <Link
        to="/"
        data-testid="briefing-back"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to home
      </Link>

      <div className="mt-8 border-b border-border pb-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">
          <Calendar className="h-4 w-4" strokeWidth={1.75} /> Executive briefing
        </div>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
          Request an executive briefing
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          Tell us a little about your program and we'll come back by email to confirm a time. No
          calendar account or payment needed — every engagement is proposal-based.
        </p>
      </div>

      <div className="grid gap-10 pt-10 lg:grid-cols-[1fr_320px] lg:gap-16">
        {/* Form */}
        <div>
          {done ? (
            <div data-testid="briefing-success" className="border border-emerald-500/30 bg-emerald-500/5 p-8">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-xl font-medium">Briefing request received</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Thank you, {form.name.split(" ")[0]}. We've sent a confirmation to{" "}
                <strong>{form.email}</strong> and our team will reply by email to confirm a time that
                suits. No account or payment is required.
              </p>
              <Button
                asChild
                data-testid="briefing-back-catalogue"
                className="mt-6 rounded-sm bg-[hsl(var(--brand))] text-white hover:bg-[hsl(217_91%_53%)]"
              >
                <Link to="/catalogue">Browse the catalogue</Link>
              </Button>
            </div>
          ) : (
            <form data-testid="briefing-form" onSubmit={submit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bf-name">Full name *</Label>
                  <Input id="bf-name" data-testid="briefing-name" value={form.name}
                    onChange={(e) => set("name", e.target.value)} className="rounded-sm" autoCapitalize="words" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bf-email">Work email *</Label>
                  <Input id="bf-email" type="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
                    data-testid="briefing-email" value={form.email}
                    onChange={(e) => set("email", e.target.value)} className="rounded-sm" />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bf-org">Organisation *</Label>
                  <Input id="bf-org" data-testid="briefing-org" value={form.organisation}
                    onChange={(e) => set("organisation", e.target.value)} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Organisation type *</Label>
                  <Select value={form.org_type} onValueChange={(v) => set("org_type", v)}>
                    <SelectTrigger data-testid="briefing-org-type" className="rounded-sm">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bf-role">Your role</Label>
                  <Input id="bf-role" data-testid="briefing-role" value={form.role}
                    onChange={(e) => set("role", e.target.value)} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Attendees</Label>
                  <Select value={form.attendees} onValueChange={(v) => set("attendees", v)}>
                    <SelectTrigger data-testid="briefing-attendees" className="rounded-sm">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {ATTENDEES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Preferred format</Label>
                  <Select value={form.format} onValueChange={(v) => set("format", v)}>
                    <SelectTrigger data-testid="briefing-format" className="rounded-sm">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Preferred timeframe</Label>
                  <Select value={form.timeframe} onValueChange={(v) => set("timeframe", v)}>
                    <SelectTrigger data-testid="briefing-timeframe" className="rounded-sm">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bf-cover">What would you like the briefing to cover? *</Label>
                <Textarea id="bf-cover" data-testid="briefing-cover" rows={3} value={form.cover}
                  onChange={(e) => set("cover", e.target.value)} className="rounded-sm"
                  placeholder="e.g. governance readiness for a new data platform, Indigenous data obligations, procurement approach…" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bf-challenge">Your biggest governance challenge right now?</Label>
                <Textarea id="bf-challenge" data-testid="briefing-challenge" rows={3} value={form.challenge}
                  onChange={(e) => set("challenge", e.target.value)} className="rounded-sm" />
              </div>

              <label className="flex cursor-pointer items-start gap-3 border border-border bg-secondary/50 p-4">
                <Checkbox data-testid="briefing-consent" checked={form.consent}
                  onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
                <span className="text-xs leading-relaxed text-muted-foreground">
                  I consent to BIGTEK storing these details for the sole purpose of arranging and
                  following up on this briefing. My data is not sold, not used for AI training, and not
                  shared with third-party advertisers.
                </span>
              </label>

              <Button type="submit" data-testid="briefing-submit" disabled={loading}
                className="h-12 w-full rounded-sm bg-[hsl(var(--brand))] text-white hover:bg-[hsl(217_91%_53%)] sm:w-auto sm:px-10">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Request briefing"}
              </Button>

              <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-500">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                Minimal fields only. We store your request for up to 24 months to arrange the briefing,
                then delete it. No third-party analytics run on this form.
              </p>
            </form>
          )}
        </div>

        {/* Pricing / includes */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="border border-border">
            <div className="border-b border-border bg-[hsl(222_47%_7%)] p-6 text-white">
              <div className="text-[10px] uppercase tracking-widest-xs text-slate-400">Indicative pricing</div>
              <div className="mt-2 font-mono text-lg font-medium">From $9,500 + GST</div>
              <p className="mt-3 text-xs leading-relaxed text-slate-400">
                90-minute executive briefing. Indicative only, subject to scope, excludes GST.
                Community-controlled pathway available on application.
              </p>
            </div>
            <div className="p-6">
              <div className="text-xs uppercase tracking-widest-xs text-muted-foreground">What's included</div>
              <ul className="mt-4 space-y-3">
                {INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={1.75} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
